import { FormEvent, useEffect, useRef, useState } from 'react'
import { trackInternalEvent } from '@rio/tracking'
import { useDispatch, useSelector } from 'react-redux'
import { useFeaturesConfig } from 'builder/hooks/featureConfig/useFeaturesConfig'
import { selectors, actions } from 'builder/modules/virtualCoach/virtualCoachModule'
import { State, LayoutVariant } from './types'

export function useVirtualCoach() {
  const dispatch = useDispatch()

  const messages = useSelector(selectors.messages)
  const loading = useSelector(selectors.loading)
  const error = useSelector(selectors.error)

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    e.stopPropagation()

    const form = e.currentTarget
    const formData = new FormData(form)
    const prompt = formData.get('prompt') as string

    dispatch(actions.sendMessage(prompt))

    form.reset()
  }

  return { messages, handleSubmit, loading, error }
}

export function useChatScroll(props: { open: boolean; messages: State }) {
  const [showScrollIndicator, setShowScrollIndicator] = useState(false)
  const chatBodyRef = useRef<HTMLDivElement | null>(null)

  const scrollToBottom = () => {
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTo({
        top: chatBodyRef.current.scrollHeight,
        behavior: 'smooth',
      })
    }
  }

  const trackScroll = () => {
    if (chatBodyRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = chatBodyRef.current
      const isAtBottom = scrollTop + clientHeight > scrollHeight - 100

      setShowScrollIndicator(!isAtBottom)
    }
  }

  useEffect(() => {
    if (props.open && chatBodyRef.current) {
      scrollToBottom()
    }
  }, [props.messages, props.open])

  return { chatBodyRef, showScrollIndicator, trackScroll, scrollToBottom }
}

export function useLayoutVariant(variant: LayoutVariant) {
  const [layoutVariant, setLayoutVariant] = useState<LayoutVariant>(variant)
  const prevLayoutRef = useRef<LayoutVariant | null>(null)

  const toggleFullScreen = () => {
    if (layoutVariant === 'fullscreen') {
      setLayoutVariant(prevLayoutRef.current || 'default')
      trackInternalEvent('chat_collapsed')
      prevLayoutRef.current = null
    } else {
      setLayoutVariant('fullscreen')
      trackInternalEvent('chat_expanded')
      prevLayoutRef.current = layoutVariant
    }
  }

  return {
    layoutVariant,
    toggleFullScreen,
  }
}

export function useVirtualCoachFeature() {
  const { features } = useFeaturesConfig()
  const isVirtualCoachEnabled = Boolean(features.aiCoach)

  return { isVirtualCoachEnabled }
}
